import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FolderKanban, CheckSquare, Users, BarChart3, Calendar, Clock, Milestone, Briefcase, MoreHorizontal, ClipboardList } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { useAuth } from '../contexts/AuthContext';

export const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();
  const [activePage, setActivePage] = useState(0);

  const handlers = useSwipeable({
    onSwipedLeft: () => setActivePage(1),
    onSwipedRight: () => setActivePage(0),
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 50,
  });

  if (!isAuthenticated) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && (location.pathname === path || location.pathname.startsWith(path + '/'))) return true;
    return false;
  };

  // Two pages of navigation items (matching desktop menu order)
  const navPages = [
    // Page 0: Main navigation (matches desktop top-level items)
    [
      { to: '/', icon: Home, label: 'Dashboard' },
      { to: '/projects', icon: FolderKanban, label: 'Projects' },
      { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
      { to: '/users', icon: Users, label: 'Users' },
      { to: '/reports', icon: BarChart3, label: 'Reports' },
    ],
    // Page 1: Planning & More (matches desktop dropdown items)
    [
      { to: '/weekly-planner', icon: Calendar, label: 'Planner' },
      { to: '/timesheets', icon: Clock, label: 'Timesheets' },
      { to: '/weekly-report', icon: ClipboardList, label: 'Wk Report' },
      { to: '/programmes', icon: Briefcase, label: 'Programmes' },
      { to: '/milestones', icon: Milestone, label: 'Milestones' },
      ...(isAdmin ? [{ to: '/admin/settings', icon: MoreHorizontal, label: 'Admin' }] : [{ to: '/notification-preferences', icon: MoreHorizontal, label: 'Settings' }]),
    ],
  ];

  return (
    <nav
      {...handlers}
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-t border-gray-200 dark:border-gray-700"
    >
      {/* Page Indicators */}
      <div className="flex gap-1.5 justify-center pt-1.5 pb-0.5">
        {navPages.map((_, index) => (
          <button
            key={index}
            onClick={() => setActivePage(index)}
            className={`h-1 rounded-full transition-all duration-300 ${
              index === activePage 
                ? 'w-6 bg-primary-600 dark:bg-primary-400' 
                : 'w-1.5 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
            aria-label={`Go to page ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Items */}
      <div className="flex items-center justify-around px-2 py-2 safe-area-bottom">
        {navPages[activePage].map(({ to, icon: Icon, label }) => (
          <Link
            key={to}
            to={to}
            className={`
              flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg flex-1 max-w-[80px]
              transition-all duration-200
              ${isActive(to)
                ? 'text-primary-600 dark:text-primary-400 scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }
            `}
          >
            <Icon
              size={22}
              className={`transition-transform ${isActive(to) ? 'scale-110' : ''}`}
            />
            <span className="text-[10px] font-medium truncate w-full text-center">
              {label}
            </span>
          </Link>
        ))}
      </div>
    </nav>
  );
};
